/**
 * parseAssignmentsList — splits a `SET col = expr, col = expr` body (or any
 * comma-separated `target = value` list) into `{ target, value }` rows. Pure /
 * structural — uses `tokenSkip` so commas inside string literals / paren groups
 * / function calls are not treated as separators.
 */

import type { ParseOptions } from '../../../../sql-grammar/model';
import { skipContextFor, trySkipNonCode } from '../../../../sql-resolver/classify/tokenSkip';
import { codeEndBeforeTrailingComments } from '../../../../sql-resolver/structure/scanner';
import { skipWsAndComments } from './insertParseHelpers';
import { splitTopLevelCommas } from '../select/splitTopLevel';

export interface AssignmentRow {
  readonly target: string;
  /** Everything after `=`, trimmed — trailing comments included. */
  readonly value: string;
  /** `value` without its trailing comments (`1 --ar applied` → `1`): what a value edit replaces. */
  readonly expression: string;
  /** The trailing comments (`--ar applied`), or '' — shown as a comment, kept on edit. */
  readonly comment: string;
  readonly start: number;
  readonly end: number;
}

/**
 * Parses an assignment-list body of the shape `col = expr [, col = expr]…`.
 * The optional leading `SET` keyword can be present in the input range — when
 * detected it's stripped off automatically. Splits at top-level commas only.
 */
export function parseAssignmentsList(
  source: string,
  start: number,
  end: number,
  options: ParseOptions,
): AssignmentRow[] {
  const ctx = skipContextFor(options);
  let cursor = skipWsAndComments(source, start, end);
  const setProbe = readWord(source, cursor, end);
  if (setProbe && setProbe.upper === 'SET') {
    cursor = setProbe.end;
  }

  const items = splitTopLevelCommas(source, cursor, end, options);
  const rows: AssignmentRow[] = [];
  for (const item of items) {
    const split = splitFirstTopLevelEquals(source, item.start, item.end, ctx);
    if (split === null) continue;
    const target = source.slice(item.start, split.eqStart).trim();
    const value = source.slice(split.eqEnd, item.end).trim();
    const codeEnd = codeEndBeforeTrailingComments(source, split.eqEnd, item.end, options);
    const expression = source.slice(split.eqEnd, codeEnd).trim();
    const comment = source.slice(codeEnd, item.end).trim();
    rows.push({ target, value, expression, comment, start: item.start, end: item.end });
  }
  return rows;
}

interface EqSplit {
  readonly eqStart: number;
  readonly eqEnd: number;
}

function splitFirstTopLevelEquals(
  source: string,
  start: number,
  end: number,
  ctx: ReturnType<typeof skipContextFor>,
): EqSplit | null {
  let i = start;
  let paren = 0;
  while (i < end) {
    const skipped = trySkipNonCode(source, i, end, ctx);
    if (skipped >= 0) { i = skipped; continue; }
    const c = source[i];
    if (c === '(') { paren++; i++; continue; }
    if (c === ')') { if (paren > 0) paren--; i++; continue; }
    if (c === '=' && paren === 0) {
      // `:=` (some dialects) / `==` (sqlite tolerates) — treat as the same eq.
      let eqEnd = i + 1;
      if (source[eqEnd] === '=') eqEnd++;
      return { eqStart: i, eqEnd };
    }
    i++;
  }
  return null;
}

function readWord(source: string, from: number, end: number): { upper: string; start: number; end: number } | null {
  if (from >= end) return null;
  if (!/[A-Za-z_]/.test(source[from])) return null;
  let j = from + 1;
  while (j < end && /[A-Za-z0-9_$]/.test(source[j])) j++;
  return { upper: source.slice(from, j).toUpperCase(), start: from, end: j };
}
